"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import fallbackHeroImg from "@/assets/images/hero/hero-location.webp";
import { Icon } from "@/components/ui/Icon";
import { motion } from "framer-motion";
import type { Locale } from "@/config/i18n";
import { HERO_SECTION, vehiclesBasePath } from "./locationHubContent";
import { tr } from "@/config/localeText";

const brands = [
  {
    name: "Audi",
    icon: "simple-icons:audi",
  },
  {
    name: "Lamborghini",
    icon: "simple-icons:lamborghini",
  },
  {
    name: "Mercedes",
    icon: "simple-icons:mercedes",
  },
  {
    name: "Porsche",
    icon: "simple-icons:porsche",
  },
  {
    name: "Rolls-Royce",
    icon: "simple-icons:rollsroyce",
  },
  {
    name: "Ferrari",
    icon: "simple-icons:ferrari",
  },
];

const HeroSection = ({ locale = "fr" }: { locale?: Locale }): JSX.Element => {
  const chrome = tr(HERO_SECTION, locale);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // La video ne part qu'APRES le chargement de la page : elle ne dispute pas la
  // bande passante a l'image du hero (LCP mobile). Rien du tout en mode
  // economie de donnees ou si le visiteur a demande moins d'animations.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const connection = (
      navigator as Navigator & {
        connection?: { saveData?: boolean };
      }
    ).connection;
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (connection?.saveData || reduceMotion) return;

    let started = false;
    const play = () => {
      video.play().catch(() => {
        // Lecture refusee par le navigateur : l'image reste affichee.
      });
    };
    const start = () => {
      started = true;
      // React ne pose pas la propriete `muted` : sans elle, le navigateur
      // refuse la lecture automatique.
      video.defaultMuted = true;
      video.muted = true;
      video.preload = "auto";
      play();
    };
    // Un onglet ouvert en arriere-plan met la video en pause sans la relancer :
    // on la relance quand la page redevient visible.
    const onVisible = () => {
      if (started && !document.hidden && video.paused) play();
    };
    document.addEventListener("visibilitychange", onVisible);
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
    return () => {
      window.removeEventListener("load", start);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  return (
    <div className="relative h-[calc(100vh-48px)] w-full overflow-hidden">
      {/* Background media: fallback image + video + overlays */}
      <div className="absolute inset-0 z-0">
        <Image
          src={fallbackHeroImg}
          alt={chrome.imageAlt}
          fill
          priority
          className="object-cover object-center opacity-70"
        />
        {/* Montage Croisette (Rolls, Porsche), heberge sur le site : plus de
            lecteur YouTube tiers ni de cookies poses sans consentement. */}
        <video
          ref={videoRef}
          aria-hidden="true"
          muted
          loop
          playsInline
          preload="none"
          poster={fallbackHeroImg.src}
          onPlaying={() => setIsVideoLoaded(true)}
          className={`pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${isVideoLoaded ? "opacity-100" : "opacity-0"}`}
        >
          <source
            src="/video/ks-location-montage-croisette.mp4"
            type="video/mp4"
          />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80" />
        <div className="absolute inset-0 bg-black/20" />
      </div>

      {/* Content */}
      <motion.div
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 mx-auto flex h-full max-w-7xl flex-col items-start justify-center px-6 py-16 sm:px-8"
      >
        <motion.h1
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="font-space-age text-3xl font-semibold tracking-wide text-gold-200 sm:text-4xl md:text-5xl"
        >
          <span className="">{chrome.titleLead}</span>
          {chrome.titleRest}
        </motion.h1>
        <motion.p
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-10 max-w-2xl text-base leading-relaxed text-white/85 sm:text-lg"
        >
          {chrome.description}
        </motion.p>

        <div className="flex flex-col items-start justify-center gap-6 md:flex-row md:items-center">
          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.25 }}
            className="mt-10 flex flex-wrap items-center gap-4 [mask-image:linear-gradient(to_left,transparent,black_10%,black_90%,transparent)]"
          >
            {brands.map((brand, index) => (
              <motion.div
                key={brand.name}
                whileHover={{ y: -3, rotateY: 8, scale: 1.08 }}
                transition={{ duration: 0.2 }}
              >
                <motion.div
                  animate={{ y: [0, -3, 0] }}
                  transition={{
                    duration: 2.8,
                    repeat: Infinity,
                    delay: index * 0.08,
                  }}
                >
                  <Icon
                    icon={brand.icon}
                    className="h-8 w-8 text-gold-200 transition-colors duration-300 hover:text-secondaryLoc"
                  />
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
          {/* CTA Button */}
          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.35 }}
          >
            <Link
              href={vehiclesBasePath(locale)}
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-gold-200 px-6 py-3 text-sm font-semibold text-[#3b0e0e] transition-all duration-300 hover:bg-white hover:shadow-lg"
            >
              {chrome.ctaFleet}
              <Icon
                icon="material-symbols:arrow-forward-rounded"
                className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default HeroSection;
